import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useModal } from '../../context/ModalContext';
import {
  getUsers,
  getUser,
  createUser,
  updateUser,
  deleteUser,
  approveUser,
  rejectUser,
  activateUser,
  deactivateUser,
  blockUser,
  unblockUser,
  bulkActionUsers,
  clearApiMessage,
  clearError,
  clearUserState,
  selectUsers,
  selectUserPagination,
  selectCurrentUser,
  selectCurrentUserPermissions,
  selectCurrentUserActivity,
  selectUsersLoading,
  selectUsersApiMessage,
  selectUsersError,
} from '../../store/slices/userSlice';
import {
  selectRoles,
  getRoles,
} from '../../store/slices/roleSlice';
import {
  selectOrganizations,
  getOrganizations,
} from '../../store/slices/organizationSlice';
import { 
  exportToCSV, 
  exportToJSON, 
  exportToExcel, 
  exportToPDF,
  formatForExport,
  getExportFilename 
} from '../../utils/exportUtils';
import { shouldShowApiToast } from '../../utils/helpers';
import { Lock, Unlock, UserPlus, User as UserIcon, Users, Eye, EyeOff, XCircle, Ban, Pencil, Trash2, Save, AlertTriangle, Clock, Search, Loader2, CheckCircle2, Pause, Play, ClipboardList, Download, BarChart3, TrendingUp, FileText, Landmark, Calendar, Key, LayoutGrid, List } from 'lucide-react';

const User = ({ showToast }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { openModal, closeModal } = useModal();
  
  // ==================== STATE ====================
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [orgFilter, setOrgFilter] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(8);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // ==================== REFS ====================
  const firstNameRef = useRef(null);
  const lastNameRef = useRef(null);
  const emailRef = useRef(null);
  const phoneRef = useRef(null);
  const roleRef = useRef(null);
  const organizationRef = useRef(null);
  const departmentRef = useRef(null);
  const statusRef = useRef(null);
  const passwordRef = useRef(null);
  const passwordConfirmationRef = useRef(null);
  const editingIdRef = useRef(null);

  // Track last toast message to prevent duplicates
  const lastToastRef = useRef({ message: null, timestamp: 0 });

  // ==================== SELECTORS ====================
  const users = useSelector(selectUsers) || [];
  const pagination = useSelector(selectUserPagination) || {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0,
    stats: {
      total: 0,
      active: 0,
      pending: 0,
      inactive: 0,
      blocked: 0
    }
  };
  const currentUser = useSelector(selectCurrentUser);
  const currentUserPermissions = useSelector(selectCurrentUserPermissions) || [];
  const currentUserActivity = useSelector(selectCurrentUserActivity) || [];
  const loading = useSelector(selectUsersLoading);
  const apiMessage = useSelector(selectUsersApiMessage);
  const error = useSelector(selectUsersError);

  // Role & Permission selectors
  const roles = useSelector(selectRoles) || [];
  const organizations = useSelector(selectOrganizations) || [];

  // ==================== HELPER: Get non-Super Admin roles ====================
  const getAvailableRoles = () => {
    return roles.filter(role => role.slug !== 'super_admin');
  };

  // ==================== EFFECTS ====================

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        await Promise.all([
          dispatch(getUsers({ per_page: itemsPerPage, page: currentPage })),
          dispatch(getRoles()),
          dispatch(getOrganizations())
        ]);
      } catch (err) {
        console.error('Error loading user data:', err);
      }
    };
    
    loadData();
    
    return () => {
      dispatch(clearUserState());
    };
  }, [dispatch]);

  // Handle search and filters
  useEffect(() => {
    const timer = setTimeout(() => {
      const params = {
        search: searchTerm,
        per_page: itemsPerPage,
        page: currentPage
      };
      if (roleFilter) params.role = roleFilter;
      if (statusFilter) params.status = statusFilter;
      if (orgFilter) params.organization_id = orgFilter;
      
      dispatch(getUsers(params));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, roleFilter, statusFilter, orgFilter, currentPage, itemsPerPage, dispatch]);

  // Display API messages - with deduplication (matching Organization pattern)
  useEffect(() => {
    if (!apiMessage || !showToast || !shouldShowApiToast(apiMessage, !!error)) return;
    
    const messageKey = `${apiMessage}_${error ? 'error' : 'success'}`;
    const now = Date.now();
    
    if (lastToastRef.current.message === messageKey && 
        (now - lastToastRef.current.timestamp) < 2000) {
      return;
    }
    
    lastToastRef.current = { message: messageKey, timestamp: now };
    
    const toastType = error ? 'error' : 'success';
    showToast(apiMessage, toastType);
    
    setTimeout(() => {
      dispatch(clearApiMessage());
      dispatch(clearError());
    }, 500);
  }, [apiMessage, error, dispatch, showToast]);

  // ==================== TOAST HELPER ====================
  const showToastMessage = useCallback((message, type = 'info') => {
    if (showToast && typeof showToast === 'function') {
      showToast(message, type);
    }
  }, [showToast]);

  // ==================== HELPER FUNCTIONS ====================

  const getFullName = (user) => {
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user.name || user.first_name || 'Unknown';
  };

  const getInitials = (user) => {
    if (user.first_name && user.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    if (user.first_name) {
      return user.first_name[0].toUpperCase();
    }
    if (user.name) {
      return user.name.split(' ').map(n => n[0]).join('').toUpperCase();
    }
    return 'U';
  };

  const getRoleById = (roleId) => {
    return roles.find(r => r.id === roleId);
  };

  const getRoleBadge = (roleId) => {
    const role = getRoleById(roleId);
    return role ? (
      <span className="badge" style={{ 
        background: `${role.color}18`, 
        color: role.color,
        fontSize: '11px'
      }}>
        {role.icon} {role.name}
        {role.is_system && (
          <span style={{ fontSize: '8px', marginLeft: '2px' }}><Lock size={8} style={{ verticalAlign: '-1px' }} /></span>
        )}
      </span>
    ) : null;
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      active: { class: 'badge-green', label: 'Active' },
      pending: { class: 'badge-amber', label: 'Pending' },
      inactive: { class: 'badge-navy', label: 'Inactive' },
      blocked: { class: 'badge-red', label: 'Blocked' }
    };
    const s = statusMap[status] || statusMap.inactive;
    return <span className={`badge ${s.class}`}>{s.label}</span>;
  };

  const getOrganizationName = (orgId) => {
    const org = organizations.find(o => o.id === orgId);
    return org ? org.name : 'N/A';
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validatePhone = (phone) => {
    const re = /^\+?[0-9]{8,15}$/;
    return re.test(phone.replace(/\s/g, ''));
  };

  const validatePassword = (password) => {
    if (!password || password.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!/[0-9]/.test(password)) {
      return 'Password must contain at least one number';
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      return 'Password must contain at least one special character';
    }
    return null;
  };

  // ==================== SELECTION HANDLERS ====================

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedUsers(users.map(u => u.id));
      setSelectAll(true);
    } else {
      setSelectedUsers([]);
      setSelectAll(false);
    }
  };

  const handleSelectUser = (id) => {
    if (selectedUsers.includes(id)) {
      setSelectedUsers(selectedUsers.filter(u => u !== id));
    } else {
      setSelectedUsers([...selectedUsers, id]);
    }
  };

  // ==================== FORM HELPERS ====================
  const getCreateFormValues = () => {
    return {
      first_name: firstNameRef.current?.value?.trim() || '',
      last_name: lastNameRef.current?.value?.trim() || '',
      email: emailRef.current?.value?.trim() || '',
      phone: phoneRef.current?.value?.trim() || '',
      role_id: roleRef.current?.value || '',
      organization_id: organizationRef.current?.value || '',
      department: departmentRef.current?.value || '',
      status: statusRef.current?.value || 'active',
      password: passwordRef.current?.value || '',
      password_confirmation: passwordConfirmationRef.current?.value || '',
    };
  };

  const getEditFormValues = () => {
    return {
      first_name: firstNameRef.current?.value?.trim() || '',
      last_name: lastNameRef.current?.value?.trim() || '',
      email: emailRef.current?.value?.trim() || '',
      phone: phoneRef.current?.value?.trim() || '',
      role_id: roleRef.current?.value || '',
      organization_id: organizationRef.current?.value || '',
      department: departmentRef.current?.value || '',
      status: statusRef.current?.value || 'active',
    };
  };

  const validateUserForm = (values, isEdit = false) => {
    const errors = {};
    
    if (!values.first_name) {
      errors.first_name = 'First name is required';
    }

    if (!values.last_name) {
      errors.last_name = 'Last name is required';
    }

    if (!values.email) {
      errors.email = 'Email is required';
    } else if (!validateEmail(values.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!values.phone) {
      errors.phone = 'Phone number is required';
    } else if (!validatePhone(values.phone)) {
      errors.phone = 'Please enter a valid phone number';
    }

    if (!values.role_id) {
      errors.role_id = 'Please select a role';
    }

    if (!values.organization_id) {
      errors.organization_id = 'Please select an organization';
    }

    if (!values.department) {
      errors.department = 'Please select a department';
    }

    // Only validate password for new users (NOT for edit)
    if (!isEdit) {
      if (!values.password) {
        errors.password = 'Password is required';
      } else {
        const passwordError = validatePassword(values.password);
        if (passwordError) {
          errors.password = passwordError;
        }
      }

      if (!values.password_confirmation) {
        errors.password_confirmation = 'Please confirm your password';
      } else if (values.password !== values.password_confirmation) {
        errors.password_confirmation = 'Passwords do not match';
      }
    }

    return errors;
  };

  // ==================== CRUD OPERATIONS ====================

  const openAddUser = () => {
    setIsEditMode(false);
    setEditingUserId(null);

    setTimeout(() => {
      if (firstNameRef.current) firstNameRef.current.value = '';
      if (lastNameRef.current) lastNameRef.current.value = '';
      if (emailRef.current) emailRef.current.value = '';
      if (phoneRef.current) phoneRef.current.value = '';
      if (roleRef.current) roleRef.current.value = '';
      if (organizationRef.current) organizationRef.current.value = '';
      if (departmentRef.current) departmentRef.current.value = '';
      if (statusRef.current) statusRef.current.value = 'active';
      if (passwordRef.current) passwordRef.current.value = '';
      if (passwordConfirmationRef.current) passwordConfirmationRef.current.value = '';
      setPasswordError('');
    }, 0);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><UserPlus size={16} style={{ verticalAlign: '-3px' }} /> Add New User</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">First Name <span style={{ color: 'var(--red)' }}>*</span></label>
              <input 
                className="form-input"
                placeholder="John"
                ref={firstNameRef}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Last Name <span style={{ color: 'var(--red)' }}>*</span></label>
              <input 
                className="form-input"
                placeholder="Doe"
                ref={lastNameRef}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Email <span style={{ color: 'var(--red)' }}>*</span></label>
            <input 
              className="form-input"
              placeholder="john@church.org"
              type="email"
              ref={emailRef}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Phone Number <span style={{ color: 'var(--red)' }}>*</span></label>
            <input 
              className="form-input"
              placeholder="+233244123456"
              ref={phoneRef}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Role <span style={{ color: 'var(--red)' }}>*</span></label>
            <select 
              className="form-select"
              ref={roleRef}
              defaultValue=""
            >
              <option value="">Select Role</option>
              {getAvailableRoles().map(r => (
                <option key={r.id} value={r.id}>{r.icon} {r.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Organization <span style={{ color: 'var(--red)' }}>*</span></label>
            <select 
              className="form-select"
              ref={organizationRef}
              defaultValue=""
            >
              <option value="">Select Organization</option>
              {organizations.map(o => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Department <span style={{ color: 'var(--red)' }}>*</span></label>
            <select 
              className="form-select"
              ref={departmentRef}
              defaultValue=""
            >
              <option value="">Select Department</option>
              <option value="Leadership">Leadership</option>
              <option value="Administration">Administration</option>
              <option value="Youth Ministry">Youth Ministry</option>
              <option value="Women's Ministry">Women's Ministry</option>
              <option value="Prayer Team">Prayer Team</option>
              <option value="Choir">Choir</option>
              <option value="Ushers">Ushers</option>
              <option value="Media">Media</option>
              <option value="Finance">Finance</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Status</label>
            <select 
              className="form-select"
              ref={statusRef}
              defaultValue="active"
            >
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          
          {/* Password Fields */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Password <span style={{ color: 'var(--red)' }}>*</span></label>
              <div style={{ position: 'relative' }}>
                <input 
                  className="form-input"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter password"
                  ref={passwordRef}
                  onChange={() => setPasswordError('')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '16px',
                    padding: '4px',
                    color: 'var(--text-light)'
                  }}
                >
                  {showPassword ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
              </div>
              <div style={{ fontSize: '11px', color: 'var(--text-light)', marginTop: '4px' }}>
                Must be at least 8 characters, contain uppercase, lowercase, number, and special character
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Confirm Password <span style={{ color: 'var(--red)' }}>*</span></label>
              <div style={{ position: 'relative' }}>
                <input 
                  className="form-input"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm password"
                  ref={passwordConfirmationRef}
                  onChange={() => setPasswordError('')}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '16px',
                    padding: '4px',
                    color: 'var(--text-light)'
                  }}
                >
                  {showConfirmPassword ? <Eye size={16} /> : <EyeOff size={16} />}
                </button>
              </div>
            </div>
          </div>
          {passwordError && (
            <div style={{ fontSize: '12px', color: 'var(--red)', marginTop: '-8px', marginBottom: '12px' }}>
              {passwordError}
            </div>
          )}

          <div style={{ padding: '12px', background: 'var(--green-bg)', borderRadius: '8px', fontSize: '13px', color: 'var(--green)' }}>
            <CheckCircle2 size={14} style={{ verticalAlign: '-2px' }} /> User will receive a welcome email with login credentials. Permissions are automatically assigned based on the selected role.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              const values = getCreateFormValues();
              const errors = validateUserForm(values, false);
              
              if (Object.keys(errors).length > 0) {
                const firstError = Object.values(errors)[0];
                showToastMessage(firstError, 'error');
                if (errors.password) {
                  setPasswordError(errors.password);
                }
                return;
              }
              
              setIsSubmitting(true);
              try {
                const result = await dispatch(createUser(values));
                if (result.payload?.success) {
                  closeModal();
                  await Promise.all([
                    dispatch(getUsers({ per_page: itemsPerPage, page: currentPage })),
                    dispatch(getRoles()),
                    dispatch(getOrganizations())
                  ]);
                  showToastMessage('User added successfully!', 'success');
                }
              } catch (err) {
                const errorMessage = err.response?.data?.message || err.message || 'Failed to create user';
                showToastMessage(errorMessage, 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? (
              <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px', animation: 'spin 1s linear infinite' }} />Adding...</>
            ) : (
              'Add User'
            )}
          </button>
        </div>
      </div>
    );
  };

  const openEditUser = (user) => {
    setIsEditMode(true);
    setEditingUserId(user.id);

    setTimeout(() => {
      if (firstNameRef.current) firstNameRef.current.value = user.first_name || '';
      if (lastNameRef.current) lastNameRef.current.value = user.last_name || '';
      if (emailRef.current) emailRef.current.value = user.email || '';
      if (phoneRef.current) phoneRef.current.value = user.phone || '';
      if (roleRef.current) roleRef.current.value = user.role_id || '';
      if (organizationRef.current) organizationRef.current.value = user.organization_id || '';
      if (departmentRef.current) departmentRef.current.value = user.department || '';
      if (statusRef.current) statusRef.current.value = user.status || 'active';
      setPasswordError('');
    }, 50);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Pencil size={16} style={{ verticalAlign: '-3px' }} /> Edit User: {getFullName(user)}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">First Name <span style={{ color: 'var(--red)' }}>*</span></label>
              <input 
                className="form-input"
                placeholder="John"
                ref={firstNameRef}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Last Name <span style={{ color: 'var(--red)' }}>*</span></label>
              <input 
                className="form-input"
                placeholder="Doe"
                ref={lastNameRef}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Email <span style={{ color: 'var(--red)' }}>*</span></label>
            <input 
              className="form-input"
              placeholder="john@church.org"
              type="email"
              ref={emailRef}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Phone Number <span style={{ color: 'var(--red)' }}>*</span></label>
            <input 
              className="form-input"
              placeholder="+233244123456"
              ref={phoneRef}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Role <span style={{ color: 'var(--red)' }}>*</span></label>
            <select 
              className="form-select"
              ref={roleRef}
            >
              <option value="">Select Role</option>
              {getAvailableRoles().map(r => (
                <option key={r.id} value={r.id}>{r.icon} {r.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Organization <span style={{ color: 'var(--red)' }}>*</span></label>
            <select 
              className="form-select"
              ref={organizationRef}
            >
              <option value="">Select Organization</option>
              {organizations.map(o => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Department <span style={{ color: 'var(--red)' }}>*</span></label>
            <select 
              className="form-select"
              ref={departmentRef}
            >
              <option value="">Select Department</option>
              <option value="Leadership">Leadership</option>
              <option value="Administration">Administration</option>
              <option value="Youth Ministry">Youth Ministry</option>
              <option value="Women's Ministry">Women's Ministry</option>
              <option value="Prayer Team">Prayer Team</option>
              <option value="Choir">Choir</option>
              <option value="Ushers">Ushers</option>
              <option value="Media">Media</option>
              <option value="Finance">Finance</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Status</label>
            <select 
              className="form-select"
              ref={statusRef}
            >
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="inactive">Inactive</option>
              <option value="blocked">Blocked</option>
            </select>
          </div>
          
          <div style={{ padding: '12px', background: 'var(--blue-bg)', borderRadius: '8px', fontSize: '13px', color: 'var(--blue)' }}>
            ℹ️ Permissions are automatically assigned based on the selected role. Password cannot be changed here.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              const values = getEditFormValues();
              const errors = validateUserForm(values, true);
              
              if (Object.keys(errors).length > 0) {
                const firstError = Object.values(errors)[0];
                showToastMessage(firstError, 'error');
                return;
              }
              
              setIsSubmitting(true);
              try {
                const result = await dispatch(updateUser({
                  id: user.id,
                  data: values
                }));
                if (result.payload?.success) {
                  closeModal();
                  await Promise.all([
                    dispatch(getUsers({ per_page: itemsPerPage, page: currentPage })),
                    dispatch(getRoles()),
                    dispatch(getOrganizations())
                  ]);
                  showToastMessage('User updated successfully!', 'success');
                }
              } catch (err) {
                const errorMessage = err.response?.data?.message || err.message || 'Failed to update user';
                showToastMessage(errorMessage, 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? (
              <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px', animation: 'spin 1s linear infinite' }} />Saving...</>
            ) : (
              <><Save size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Save Changes</>
            )}
          </button>
        </div>
      </div>
    );
  };

  // ==================== USER ACTION FUNCTIONS ====================

  const handleApproveUser = (user) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><CheckCircle2 size={16} style={{ verticalAlign: '-3px' }} /> Approve User</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to approve <strong>{getFullName(user)}</strong>?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--green-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--green)' 
          }}>
            <CheckCircle2 size={14} style={{ verticalAlign: '-2px' }} /> This will activate {getFullName(user)}'s account and grant them access based on their role permissions.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                const result = await dispatch(approveUser(user.id));
                if (result.payload?.success) {
                  closeModal();
                  await Promise.all([
                    dispatch(getUsers({ per_page: itemsPerPage, page: currentPage })),
                    dispatch(getRoles()),
                    dispatch(getOrganizations())
                  ]);
                  showToastMessage(`✅ ${getFullName(user)} has been approved and activated!`, 'success');
                }
              } catch (err) {
                const errorMessage = err.response?.data?.message || err.message || 'Failed to approve user';
                showToastMessage(errorMessage, 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? (
              <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px', animation: 'spin 1s linear infinite' }} />Approving...</>
            ) : (
              'Approve User'
            )}
          </button>
        </div>
      </div>
    );
  };

  const handleRejectUser = (user) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><XCircle size={16} style={{ verticalAlign: '-3px' }} /> Reject User</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to reject <strong>{getFullName(user)}</strong>'s registration?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--red-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--red)' 
          }}>
            <AlertTriangle size={14} style={{ verticalAlign: '-2px' }} /> This will deny access to {getFullName(user)} and they will not be able to use the platform.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-danger btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                const result = await dispatch(rejectUser(user.id));
                if (result.payload?.success) {
                  closeModal();
                  await Promise.all([
                    dispatch(getUsers({ per_page: itemsPerPage, page: currentPage })),
                    dispatch(getRoles()),
                    dispatch(getOrganizations())
                  ]);
                  showToastMessage(`❌ ${getFullName(user)} has been rejected.`, 'error');
                }
              } catch (err) {
                const errorMessage = err.response?.data?.message || err.message || 'Failed to reject user';
                showToastMessage(errorMessage, 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? (
              <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px', animation: 'spin 1s linear infinite' }} />Rejecting...</>
            ) : (
              'Reject User'
            )}
          </button>
        </div>
      </div>
    );
  };

  const handleActivateUser = (user) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Play size={16} style={{ verticalAlign: '-3px' }} /> Activate User</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to activate <strong>{getFullName(user)}</strong>'s account?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--green-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--green)' 
          }}>
            <CheckCircle2 size={14} style={{ verticalAlign: '-2px' }} /> {getFullName(user)} will regain full access to the platform based on their role permissions.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                const result = await dispatch(activateUser(user.id));
                if (result.payload?.success) {
                  closeModal();
                  await Promise.all([
                    dispatch(getUsers({ per_page: itemsPerPage, page: currentPage })),
                    dispatch(getRoles()),
                    dispatch(getOrganizations())
                  ]);
                  showToastMessage(`✅ ${getFullName(user)} has been activated!`, 'success');
                }
              } catch (err) {
                const errorMessage = err.response?.data?.message || err.message || 'Failed to activate user';
                showToastMessage(errorMessage, 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? (
              <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px', animation: 'spin 1s linear infinite' }} />Activating...</>
            ) : (
              'Activate User'
            )}
          </button>
        </div>
      </div>
    );
  };

  const handleDeactivateUser = async (user) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Pause size={16} style={{ verticalAlign: '-3px' }} /> Deactivate User</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to deactivate <strong>{getFullName(user)}</strong>'s account?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--amber-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--amber)' 
          }}>
            <AlertTriangle size={14} style={{ verticalAlign: '-2px' }} /> {getFullName(user)} will lose access to the platform until reactivated.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-deactivate btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                const result = await dispatch(deactivateUser(user.id));
                if (result.payload?.success) {
                  closeModal();
                  await Promise.all([
                    dispatch(getUsers({ per_page: itemsPerPage, page: currentPage })),
                    dispatch(getRoles()),
                    dispatch(getOrganizations())
                  ]);
                  showToastMessage(`⏸️ ${getFullName(user)} has been deactivated.`, 'info');
                }
              } catch (err) {
                const errorMessage = err.response?.data?.message || err.message || 'Failed to deactivate user';
                showToastMessage(errorMessage, 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? (
              <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px', animation: 'spin 1s linear infinite' }} />Deactivating...</>
            ) : (
              'Deactivate User'
            )}
          </button>
        </div>
      </div>
    );
  };

  const handleBlockUser = (user) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Ban size={16} style={{ verticalAlign: '-3px' }} /> Block User</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to block <strong>{getFullName(user)}</strong>?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--red-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--red)' 
          }}>
            <Ban size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />{getFullName(user)} will be immediately locked out of the platform.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-danger btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                const result = await dispatch(blockUser(user.id));
                if (result.payload?.success) {
                  closeModal();
                  await Promise.all([
                    dispatch(getUsers({ per_page: itemsPerPage, page: currentPage })),
                    dispatch(getRoles()),
                    dispatch(getOrganizations())
                  ]);
                  showToastMessage(`🚫 ${getFullName(user)} has been blocked.`, 'error');
                }
              } catch (err) {
                const errorMessage = err.response?.data?.message || err.message || 'Failed to block user';
                showToastMessage(errorMessage, 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? (
              <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px', animation: 'spin 1s linear infinite' }} />Blocking...</>
            ) : (
              'Block User'
            )}
          </button>
        </div>
      </div>
    );
  };

  const handleUnblockUser = (user) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Unlock size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Unblock User</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to unblock <strong>{getFullName(user)}</strong>?
          </p>
          <div style={{ 
            padding: '12px', 
            background: 'var(--green-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: 'var(--green)' 
          }}>
            <CheckCircle2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />{getFullName(user)} will regain access to the platform.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-gold btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                const result = await dispatch(unblockUser(user.id));
                if (result.payload?.success) {
                  closeModal();
                  await Promise.all([
                    dispatch(getUsers({ per_page: itemsPerPage, page: currentPage })),
                    dispatch(getRoles()),
                    dispatch(getOrganizations())
                  ]);
                  showToastMessage(`🔓 ${getFullName(user)} has been unblocked.`, 'success');
                }
              } catch (err) {
                const errorMessage = err.response?.data?.message || err.message || 'Failed to unblock user';
                showToastMessage(errorMessage, 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? (
              <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px', animation: 'spin 1s linear infinite' }} />Unblocking...</>
            ) : (
              'Unblock User'
            )}
          </button>
        </div>
      </div>
    );
  };

  const handleDeleteUser = (user) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Trash2 size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Delete User</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ 
            textAlign: 'center',
            padding: '20px 0'
          }}>
            <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'center' }}><AlertTriangle size={48} strokeWidth={1.5} /></div>
            <div style={{ fontSize: '18px', fontWeight: '600', color: 'var(--red)' }}>
              Delete {getFullName(user)}?
            </div>
            <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginTop: '8px' }}>
              This action cannot be undone. All user data and permissions will be permanently removed.
            </p>
            <div style={{ 
              marginTop: '16px',
              padding: '12px',
              background: 'var(--red-bg)',
              borderRadius: '8px',
              fontSize: '13px',
              color: 'var(--red)'
            }}>
              <AlertTriangle size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />This will permanently remove all user data and permissions.
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className="btn btn-danger btn-sm" 
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                const result = await dispatch(deleteUser(user.id));
                if (result.payload?.success) {
                  closeModal();
                  await Promise.all([
                    dispatch(getUsers({ per_page: itemsPerPage, page: currentPage })),
                    dispatch(getRoles()),
                    dispatch(getOrganizations())
                  ]);
                  showToastMessage(`🗑️ ${getFullName(user)} has been permanently deleted.`, 'error');
                }
              } catch (err) {
                const errorMessage = err.response?.data?.message || err.message || 'Failed to delete user';
                showToastMessage(errorMessage, 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? (
              <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px', animation: 'spin 1s linear infinite' }} />Deleting...</>
            ) : (
              'Delete User'
            )}
          </button>
        </div>
      </div>
    );
  };

  // ==================== BULK ACTIONS ====================

  const handleBulkAction = (action) => {
    if (selectedUsers.length === 0) {
      showToastMessage('Please select at least one user.', 'error');
      return;
    }

    const actionMap = {
      approve: {
        label: 'approved',
        icon: CheckCircle2,
        title: 'Approve Selected Users',
        description: 'This will activate all selected users and grant them access based on their roles.'
      },
      reject: {
        label: 'rejected',
        icon: XCircle,
        title: 'Reject Selected Users',
        description: 'This will deny access to all selected users.'
      },
      activate: {
        label: 'activated',
        icon: Play,
        title: 'Activate Selected Users',
        description: 'This will reactivate all selected users.'
      },
      deactivate: {
        label: 'deactivated',
        icon: Pause,
        title: 'Deactivate Selected Users',
        description: 'This will deactivate all selected users.'
      },
      block: {
        label: 'blocked',
        icon: Ban,
        title: 'Block Selected Users',
        description: 'This will block all selected users immediately.'
      },
      delete: {
        label: 'deleted',
        icon: Trash2,
        title: 'Delete Selected Users',
        description: 'This will permanently remove all selected users.'
      }
    };

    const actionInfo = actionMap[action];
    if (!actionInfo) return;

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><actionInfo.icon size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />{actionInfo.title}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to {action} <strong>{selectedUsers.length}</strong> selected user(s)?
          </p>
          <div style={{ 
            padding: '12px', 
            background: action === 'delete' || action === 'block' || action === 'reject' ? 'var(--red-bg)' : 'var(--amber-bg)', 
            borderRadius: '8px', 
            fontSize: '13px', 
            color: action === 'delete' || action === 'block' || action === 'reject' ? 'var(--red)' : 'var(--amber)' 
          }}>
            <AlertTriangle size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />{actionInfo.description}
          </div>
          <div style={{ 
            marginTop: '12px',
            maxHeight: '150px',
            overflowY: 'auto',
            background: 'var(--cream)',
            borderRadius: '8px',
            padding: '8px'
          }}>
            {selectedUsers.map(id => {
              const user = users.find(u => u.id === id);
              return user ? (
                <div key={id} style={{ fontSize: '12px', padding: '4px 8px', borderBottom: '1px solid var(--border-light)' }}>
                  {getFullName(user)} · {user.email}
                </div>
              ) : null;
            })}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button 
            className={`btn ${action === 'delete' || action === 'block' || action === 'reject' ? 'btn-danger' : 'btn-gold'}`} 
            disabled={isSubmitting}
            onClick={async () => {
              setIsSubmitting(true);
              try {
                const result = await dispatch(bulkActionUsers({
                  userIds: selectedUsers,
                  action: action
                }));
                if (result.payload?.success) {
                  closeModal();
                  setSelectedUsers([]);
                  setSelectAll(false);
                  await Promise.all([
                    dispatch(getUsers({ per_page: itemsPerPage, page: currentPage })),
                    dispatch(getRoles()),
                    dispatch(getOrganizations())
                  ]);
                  showToastMessage(`${actionInfo.icon} ${selectedUsers.length} user(s) ${actionInfo.label}.`, 'success');
                }
              } catch (err) {
                const errorMessage = err.response?.data?.message || err.message || 'Failed to perform bulk action';
                showToastMessage(errorMessage, 'error');
              } finally {
                setIsSubmitting(false);
              }
            }}
          >
            {isSubmitting ? (
              <><Loader2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px', animation: 'spin 1s linear infinite' }} />Processing...</>
            ) : (
              `Confirm ${action.charAt(0).toUpperCase() + action.slice(1)}`
            )}
          </button>
        </div>
      </div>
    );
  };

  // ==================== VIEW USER DETAIL ====================

  const handleViewUser = (user) => {
    dispatch(getUser(user.id));
    
    openModal(
      <div>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div className="member-row-avatar" style={{ 
              background: '#4f46e5', 
              width: '46px', 
              height: '46px', 
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              color: 'white',
              fontWeight: '600'
            }}>
              {getInitials(user)}
            </div>
            <div>
              <div className="modal-title">{getFullName(user)}</div>
              <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                {user.email} · {getRoleById(user.role_id)?.name || 'No Role'}
              </div>
            </div>
          </div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
            {[
              ['First Name', user.first_name || 'N/A'],
              ['Last Name', user.last_name || 'N/A'],
              ['Status', getStatusBadge(user.status)],
              ['Organization', getOrganizationName(user.organization_id)],
              ['Department', user.department || 'N/A'],
              ['Joined', user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'],
              ['Last Active', user.last_active || 'Never'],
              ['Email Verified', user.email_verified_at
                ? <><CheckCircle2 size={13} style={{ verticalAlign: '-2px' }} /> Yes</>
                : <><XCircle size={13} style={{ verticalAlign: '-2px' }} /> No</>]
            ].map(([l, v]) => (
              <div key={l} style={{ padding: '10px 12px', background: 'var(--cream)', borderRadius: '8px' }}>
                <div style={{ fontSize: '11px', color: 'var(--text-light)', marginBottom: '4px' }}>{l}</div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--navy)' }}>{v}</div>
              </div>
            ))}
          </div>
          <div style={{ background: 'var(--cream)', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '8px' }}>Role & Permissions</div>
            <div style={{ marginBottom: '8px' }}>
              {getRoleBadge(user.role_id)}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-mid)' }}>
              <em>Permissions are automatically assigned based on the user's role.</em>
            </div>
          </div>
          {/* Activity Log */}
          {currentUserActivity && currentUserActivity.length > 0 && (
            <div style={{ background: 'var(--cream)', borderRadius: '8px', padding: '12px' }}>
              <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '8px' }}>Recent Activity</div>
              <div style={{ maxHeight: '100px', overflowY: 'auto' }}>
                {currentUserActivity.slice(0, 5).map((activity, index) => (
                  <div key={index} style={{ fontSize: '12px', padding: '4px 0', borderBottom: '1px solid var(--border-light)' }}>
                    {activity.action} · {new Date(activity.created_at).toLocaleString()}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
          <>
            {user.status === 'pending' && (
              <>
                <button className="btn btn-approve btn-sm" onClick={() => { handleApproveUser(user); closeModal(); }}><CheckCircle2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Approve</button>
                <button className="btn btn-reject btn-sm" onClick={() => { handleRejectUser(user); closeModal(); }}><XCircle size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Reject</button>
              </>
            )}
            {user.status === 'active' && (
              <>
                <button className="btn btn-deactivate btn-sm" onClick={() => { handleDeactivateUser(user); closeModal(); }}><Pause size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Deactivate</button>
                <button className="btn btn-danger btn-sm" onClick={() => { handleBlockUser(user); closeModal(); }}><Ban size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Block</button>
              </>
            )}
            {user.status === 'inactive' && (
              <>
                <button className="btn btn-gold btn-sm" onClick={() => { handleActivateUser(user); closeModal(); }}><Play size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Activate</button>
                <button className="btn btn-danger btn-sm" onClick={() => { handleBlockUser(user); closeModal(); }}><Ban size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Block</button>
                <button className="btn btn-delete-sm" onClick={() => { closeModal(); handleDeleteUser(user); }}><Trash2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Delete</button>
              </>
            )}
            {user.status === 'blocked' && (
              <>
                <button className="btn btn-gold btn-sm" onClick={() => { handleUnblockUser(user); closeModal(); }}><Unlock size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Unblock</button>
                <button className="btn btn-delete-sm" onClick={() => { closeModal(); handleDeleteUser(user); }}><Trash2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Delete</button>
              </>
            )}
            <button className="btn btn-ghost btn-sm" onClick={() => { closeModal(); openEditUser(user); }}><Pencil size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Edit</button>
            <button className="btn btn-danger btn-sm" onClick={() => { closeModal(); handleDeleteUser(user); }}><Trash2 size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Delete</button>
          </>
        </div>
      </div>
    );
  };

  // ==================== EXPORT ====================

  const openExportModal = () => {
    if (users.length === 0) {
      showToastMessage('No users to export', 'error');
      return;
    }

    const data = users;
    const fields = [
      { key: 'id', label: 'ID' },
      { key: 'first_name', label: 'First Name' },
      { key: 'last_name', label: 'Last Name' },
      { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Phone' },
      { key: 'department', label: 'Department' },
      { key: 'position', label: 'Position' },
      { key: 'status', label: 'Status' },
      { key: 'organization_id', label: 'Organization ID' },
      { key: 'created_at', label: 'Joined Date' },
      { key: 'last_login', label: 'Last Login' }
    ];

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Download size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Export Users</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize: '13px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Export {data.length} user{data.length !== 1 ? 's' : ''} in your preferred format.
            {selectedUsers.length > 0 && (
              <span style={{ marginLeft: '8px', fontWeight: '600', color: 'var(--navy)' }}>
                · {selectedUsers.length} selected
              </span>
            )}
          </div>
          
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-mid)', marginBottom: '8px' }}>
              Select Format
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
              {['CSV', 'JSON', 'Excel', 'PDF'].map(format => (
                <button
                  key={format}
                  className="btn btn-ghost btn-sm"
                  disabled={isExporting}
                  onClick={() => {
                    setIsExporting(true);
                    const exportData = selectedUsers.length > 0 
                      ? data.filter(u => selectedUsers.includes(u.id))
                      : data;
                    const formattedData = formatForExport(exportData, fields);
                    const filename = getExportFilename('users');
                    
                    try {
                      switch (format) {
                        case 'CSV':
                          exportToCSV(formattedData, filename);
                          break;
                        case 'JSON':
                          exportToJSON(formattedData, filename);
                          break;
                        case 'Excel':
                          exportToExcel(formattedData, filename);
                          break;
                        case 'PDF':
                          exportToPDF(formattedData, 'Users Report', filename);
                          break;
                        default:
                          showToastMessage('Export format not supported', 'error');
                          setIsExporting(false);
                          return;
                      }
                      showToastMessage(`📥 ${exportData.length} users exported as ${format}!`, 'success');
                      closeModal();
                    } catch (err) {
                      showToastMessage('Failed to export', 'error');
                    } finally {
                      setIsExporting(false);
                    }
                  }}
                  style={{ 
                    padding: '8px', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    height: 'auto',
                    border: '2px solid var(--border)',
                    borderRadius: '8px',
                    transition: 'all 0.2s',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--gold)';
                    e.currentTarget.style.background = 'var(--gold-pale)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border)';
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  {isExporting ? (
                    <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
                  ) : (
                    <span style={{ display: 'flex' }}>
                      {format === 'CSV' && <BarChart3 size={20} />}
                      {format === 'JSON' && <ClipboardList size={20} />}
                      {format === 'Excel' && <TrendingUp size={20} />}
                      {format === 'PDF' && <FileText size={20} />}
                    </span>
                  )}
                  <span style={{ fontSize: '11px' }}>{format}</span>
                </button>
              ))}
            </div>
          </div>

          <div style={{ 
            padding: '10px 12px', 
            background: 'var(--cream)', 
            borderRadius: '8px',
            fontSize: '12px',
            color: 'var(--text-light)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span><BarChart3 size={12} style={{ verticalAlign: '-2px', marginRight: '3px' }} />{selectedUsers.length > 0 ? selectedUsers.length : users.length} records will be exported</span>
            {selectedUsers.length > 0 && (
              <button 
                className="btn btn-ghost btn-sm" 
                onClick={() => setSelectedUsers([])}
              >
                Clear Selection
              </button>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
        </div>
      </div>
    );
  };

  // ==================== PAGINATION ====================

  const totalItems = pagination.total || 0;
  const totalPages = pagination.last_page || 1;

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  // ==================== RENDER FUNCTIONS ====================

  const renderGridView = () => {
    return (
      <div className="user-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
        {users.map(user => (
          <div key={user.id} className="card" style={{ 
            cursor: 'pointer', 
            transition: 'transform 0.2s, box-shadow 0.2s', 
            position: 'relative', 
            borderTop: `3px solid #4f46e5` 
          }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'var(--shadow)';
            }}
            onClick={() => handleViewUser(user)}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
              <input 
                type="checkbox" 
                style={{ accentColor: 'var(--navy)', marginTop: '2px' }} 
                checked={selectedUsers.includes(user.id)} 
                onChange={() => handleSelectUser(user.id)} 
                onClick={(e) => e.stopPropagation()} 
                disabled={loading || isSubmitting}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div className="member-row-avatar" style={{ 
                  background: '#4f46e5', 
                  width: '40px', 
                  height: '40px', 
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  color: 'white',
                  fontWeight: '600'
                }}>
                  {getInitials(user)}
                </div>
                <div>
                  <div style={{ fontWeight: '600', color: 'var(--navy)' }}>{getFullName(user)}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>{user.email}</div>
                </div>
              </div>
            </div>
            
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
              {getRoleBadge(user.role_id)}
              {getStatusBadge(user.status)}
              {user.organization_id && <span className="badge badge-navy" style={{ fontSize: '10px' }}><Landmark size={11} style={{ verticalAlign: '-2px', marginRight: '3px' }} />{getOrganizationName(user.organization_id)}</span>}
            </div>

            <div style={{ fontSize: '12px', color: 'var(--text-light)', marginBottom: '10px' }}>
              <div><Calendar size={11} style={{ verticalAlign: '-2px', marginRight: '3px' }} />Joined: {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}</div>
              <div><Clock size={11} style={{ verticalAlign: '-2px', marginRight: '3px' }} />Last active: {user.last_active || 'Never'}</div>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              <button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); handleViewUser(user); }} title="View user details" disabled={loading || isSubmitting}><Eye size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />View</button>
              <button className="btn btn-ghost btn-sm btn-icon" onClick={(e) => { e.stopPropagation(); openEditUser(user); }} title="Edit user" disabled={loading || isSubmitting}><Pencil size={14} /></button>

              {user.status === 'pending' && (
                <>
                  <button
                    className="btn-approve-sm"
                    onClick={(e) => { e.stopPropagation(); handleApproveUser(user); }}
                    title="Approve user"
                    disabled={loading || isSubmitting}
                  >
                    <CheckCircle2 size={14} />
                  </button>
                  <button
                    className="btn-reject-sm"
                    onClick={(e) => { e.stopPropagation(); handleRejectUser(user); }}
                    title="Reject user"
                    disabled={loading || isSubmitting}
                  >
                    <XCircle size={14} />
                  </button>
                </>
              )}

              {user.status === 'active' && (
                <>
                  <button
                    className="btn-deactivate-sm"
                    onClick={(e) => { e.stopPropagation(); handleDeactivateUser(user); }}
                    title="Deactivate user"
                    disabled={loading || isSubmitting}
                  >
                    <Pause size={14} />
                  </button>
                  <button
                    className="btn-danger-sm"
                    onClick={(e) => { e.stopPropagation(); handleBlockUser(user); }}
                    title="Block user"
                    disabled={loading || isSubmitting}
                    style={{
                      background: 'var(--red-bg)',
                      color: 'var(--red)',
                      border: '1px solid #fca5a5',
                      padding: '4px 10px',
                      fontSize: '11px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontFamily: 'inherit'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'var(--red)';
                      e.currentTarget.style.color = 'white';
                      e.currentTarget.style.borderColor = 'var(--red)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'var(--red-bg)';
                      e.currentTarget.style.color = 'var(--red)';
                      e.currentTarget.style.borderColor = '#fca5a5';
                    }}
                  >
                    <Ban size={12} style={{ verticalAlign: '-2px', marginRight: '3px' }} />Block
                  </button>
                </>
              )}

              {user.status === 'inactive' && (
                <>
                  <button
                    className="btn-activate-sm"
                    onClick={(e) => { e.stopPropagation(); handleActivateUser(user); }}
                    title="Activate user"
                    disabled={loading || isSubmitting}
                  >
                    <Play size={14} />
                  </button>
                  <button
                    className="btn-danger-sm"
                    onClick={(e) => { e.stopPropagation(); handleBlockUser(user); }}
                    title="Block user"
                    disabled={loading || isSubmitting}
                    style={{
                      background: 'var(--red-bg)',
                      color: 'var(--red)',
                      border: '1px solid #fca5a5',
                      padding: '4px 10px',
                      fontSize: '11px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontFamily: 'inherit'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'var(--red)';
                      e.currentTarget.style.color = 'white';
                      e.currentTarget.style.borderColor = 'var(--red)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'var(--red-bg)';
                      e.currentTarget.style.color = 'var(--red)';
                      e.currentTarget.style.borderColor = '#fca5a5';
                    }}
                  >
                    <Ban size={12} style={{ verticalAlign: '-2px', marginRight: '3px' }} />Block
                  </button>
                </>
              )}

              {user.status === 'blocked' && (
                <button
                  className="btn-unblock-sm"
                  onClick={(e) => { e.stopPropagation(); handleUnblockUser(user); }}
                  title="Unblock user"
                  disabled={loading || isSubmitting}
                  style={{
                    background: 'var(--green-bg)',
                    color: 'var(--green)',
                    border: '1px solid #a7d7be',
                    padding: '4px 10px',
                    fontSize: '11px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: 'inherit'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#22c55e';
                    e.currentTarget.style.color = 'white';
                    e.currentTarget.style.borderColor = '#22c55e';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'var(--green-bg)';
                    e.currentTarget.style.color = 'var(--green)';
                    e.currentTarget.style.borderColor = '#a7d7be';
                  }}
                >
                  <Unlock size={12} style={{ verticalAlign: '-2px', marginRight: '3px' }} />Unblock
                </button>
              )}

              <button
                className="btn-delete-sm"
                onClick={(e) => { e.stopPropagation(); handleDeleteUser(user); }}
                title="Delete user permanently"
                disabled={loading || isSubmitting}
                style={{
                  color: '#ef4444',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px 10px',
                  fontSize: '12px',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#fef2f2';
                  e.currentTarget.style.color = '#ef4444';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = '#ef4444';
                }}
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderListView = () => {
    return (
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th style={{ width: '40px' }}>
                <input 
                  type="checkbox" 
                  style={{ accentColor: 'var(--navy)' }} 
                  checked={selectAll && users.length > 0}
                  onChange={handleSelectAll}
                  disabled={loading || isSubmitting}
                />
              </th>
              <th>User</th>
              <th>Email</th>
              <th>Role</th>
              <th>Organization</th>
              <th>Status</th>
              <th style={{ minWidth: '140px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id} style={{ cursor: 'pointer' }} onClick={() => handleViewUser(user)}>
                <td>
                  <input 
                    type="checkbox" 
                    style={{ accentColor: 'var(--navy)' }} 
                    checked={selectedUsers.includes(user.id)}
                    onChange={() => handleSelectUser(user.id)}
                    onClick={(e) => e.stopPropagation()}
                    disabled={loading || isSubmitting}
                  />
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div className="member-row-avatar" style={{ 
                      background: '#4f46e5', 
                      width: '32px', 
                      height: '32px', 
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      color: 'white',
                      fontWeight: '600'
                    }}>
                      {getInitials(user)}
                    </div>
                    <div>
                      <div style={{ fontWeight: '600', color: 'var(--navy)' }}>{getFullName(user)}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>Joined: {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}</div>
                    </div>
                  </div>
                </td>
                <td style={{ fontSize: '13px', color: 'var(--text-mid)' }}>{user.email}</td>
                <td>{getRoleBadge(user.role_id)}</td>
                <td style={{ fontSize: '13px', color: 'var(--text-mid)' }}>{getOrganizationName(user.organization_id)}</td>
                <td>{getStatusBadge(user.status)}</td>
                <td>
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    <button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); handleViewUser(user); }} title="View user details" disabled={loading || isSubmitting}><Eye size={14} /></button>
                    <button className="btn btn-ghost btn-sm btn-icon" onClick={(e) => { e.stopPropagation(); openEditUser(user); }} title="Edit user" disabled={loading || isSubmitting}><Pencil size={14} /></button>

                    {user.status === 'pending' && (
                      <>
                        <button
                          className="btn-approve-sm"
                          onClick={(e) => { e.stopPropagation(); handleApproveUser(user); }}
                          title="Approve user"
                          disabled={loading || isSubmitting}
                        >
                          <CheckCircle2 size={14} />
                        </button>
                        <button
                          className="btn-reject-sm"
                          onClick={(e) => { e.stopPropagation(); handleRejectUser(user); }}
                          title="Reject user"
                          disabled={loading || isSubmitting}
                        >
                          <XCircle size={14} />
                        </button>
                      </>
                    )}

                    {user.status === 'active' && (
                      <>
                        <button
                          className="btn-deactivate-sm"
                          onClick={(e) => { e.stopPropagation(); handleDeactivateUser(user); }}
                          title="Deactivate user"
                          disabled={loading || isSubmitting}
                        >
                          <Pause size={14} />
                        </button>
                        <button
                          className="btn-danger-sm"
                          onClick={(e) => { e.stopPropagation(); handleBlockUser(user); }}
                          title="Block user"
                          disabled={loading || isSubmitting}
                          style={{
                            background: 'var(--red-bg)',
                            color: 'var(--red)',
                            border: '1px solid #fca5a5',
                            padding: '4px 10px',
                            fontSize: '11px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontFamily: 'inherit'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'var(--red)';
                            e.currentTarget.style.color = 'white';
                            e.currentTarget.style.borderColor = 'var(--red)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'var(--red-bg)';
                            e.currentTarget.style.color = 'var(--red)';
                            e.currentTarget.style.borderColor = '#fca5a5';
                          }}
                        >
                          <Ban size={12} style={{ verticalAlign: '-2px', marginRight: '3px' }} />Block
                        </button>
                      </>
                    )}

                    {user.status === 'inactive' && (
                      <>
                        <button
                          className="btn-activate-sm"
                          onClick={(e) => { e.stopPropagation(); handleActivateUser(user); }}
                          title="Activate user"
                          disabled={loading || isSubmitting}
                        >
                          <Play size={14} />
                        </button>
                        <button
                          className="btn-danger-sm"
                          onClick={(e) => { e.stopPropagation(); handleBlockUser(user); }}
                          title="Block user"
                          disabled={loading || isSubmitting}
                          style={{
                            background: 'var(--red-bg)',
                            color: 'var(--red)',
                            border: '1px solid #fca5a5',
                            padding: '4px 10px',
                            fontSize: '11px',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontFamily: 'inherit'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'var(--red)';
                            e.currentTarget.style.color = 'white';
                            e.currentTarget.style.borderColor = 'var(--red)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'var(--red-bg)';
                            e.currentTarget.style.color = 'var(--red)';
                            e.currentTarget.style.borderColor = '#fca5a5';
                          }}
                        >
                          <Ban size={12} style={{ verticalAlign: '-2px', marginRight: '3px' }} />Block
                        </button>
                      </>
                    )}

                    {user.status === 'blocked' && (
                      <button
                        className="btn-unblock-sm"
                        onClick={(e) => { e.stopPropagation(); handleUnblockUser(user); }}
                        title="Unblock user"
                        disabled={loading || isSubmitting}
                        style={{
                          background: 'var(--green-bg)',
                          color: 'var(--green)',
                          border: '1px solid #a7d7be',
                          padding: '4px 10px',
                          fontSize: '11px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontFamily: 'inherit'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#22c55e';
                          e.currentTarget.style.color = 'white';
                          e.currentTarget.style.borderColor = '#22c55e';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'var(--green-bg)';
                          e.currentTarget.style.color = 'var(--green)';
                          e.currentTarget.style.borderColor = '#a7d7be';
                        }}
                      >
                        <Unlock size={12} style={{ verticalAlign: '-2px', marginRight: '3px' }} />Unblock
                      </button>
                    )}

                    <button
                      className="btn-delete-sm"
                      onClick={(e) => { e.stopPropagation(); handleDeleteUser(user); }}
                      title="Delete user permanently"
                      disabled={loading || isSubmitting}
                      style={{
                        color: '#ef4444',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px 10px',
                        fontSize: '12px',
                        borderRadius: '6px',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#fef2f2';
                        e.currentTarget.style.color = '#ef4444';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                        e.currentTarget.style.color = '#ef4444';
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {users.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon"><UserIcon size={32} strokeWidth={1.5} /></div>
            <div className="empty-title">No users found</div>
            <div className="empty-text">Try adjusting your search or filters</div>
          </div>
        )}
      </div>
    );
  };

  // ==================== MAIN RENDER ====================

  const stats = pagination.stats || {
    total: 0,
    active: 0,
    pending: 0,
    inactive: 0,
    blocked: 0
  };

  return (
    <div className="user-management">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '20px', fontWeight: '700', color: 'var(--navy)' }}>
            <UserIcon size={20} style={{ verticalAlign: '-3px', marginRight: '6px' }} />User Management
          </h2>
          <p style={{ fontSize: '14px', color: 'var(--text-mid)' }}>
            Manage users across your organization. Permissions are automatically assigned based on roles.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn btn-gold btn-sm" onClick={openAddUser} disabled={loading || isSubmitting}>
            + Add User
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/roles')} disabled={loading}>
            <Key size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Manage Roles
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '20px' }}>
        <div className="stat-card gold" style={{ cursor: 'default' }}>
          <div className="stat-label">Total Users</div>
          <div className="stat-value">{stats.total || 0}</div>
          <div className="stat-change up">All users</div>
          <div className="stat-icon"><Users size={20} /></div>
        </div>
        <div className="stat-card green" style={{ cursor: 'default' }}>
          <div className="stat-label">Active</div>
          <div className="stat-value">{stats.active || 0}</div>
          <div className="stat-change up">↑ Online</div>
          <div className="stat-icon"><CheckCircle2 size={20} /></div>
        </div>
        <div className="stat-card amber" style={{ cursor: 'default' }}>
          <div className="stat-label">Pending</div>
          <div className="stat-value">{stats.pending || 0}</div>
          <div className="stat-change down">Needs review</div>
          <div className="stat-icon"><Clock size={20} /></div>
        </div>
        <div className="stat-card red" style={{ cursor: 'default' }}>
          <div className="stat-label">Inactive/Blocked</div>
          <div className="stat-value">{(stats.inactive || 0) + (stats.blocked || 0)}</div>
          <div className="stat-change down">↓ Inactive</div>
          <div className="stat-icon"><Ban size={20} /></div>
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="filter-container" style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
          <div className="search-box" style={{ maxWidth: '300px', flex: '1' }}>
            <span className="search-icon"><Search size={16} /></span>
            <input 
              type="text" 
              placeholder="Search by name, email, phone..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={loading}
            />
          </div>
          <select className="form-select" style={{ width: '140px' }} value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} disabled={loading}>
            <option value="">All Roles</option>
            {getAvailableRoles().map(r => (
              <option key={r.id} value={r.slug}>{r.icon} {r.name}</option>
            ))}
          </select>
          <select className="form-select" style={{ width: '140px' }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} disabled={loading}>
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="inactive">Inactive</option>
            <option value="blocked">Blocked</option>
          </select>
          <select className="form-select" style={{ width: '180px' }} value={orgFilter} onChange={(e) => setOrgFilter(e.target.value)} disabled={loading}>
            <option value="">All Organizations</option>
            {organizations.map(o => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
          <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
            <button 
              className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-ghost'} btn-sm`} 
              onClick={() => setViewMode('grid')}
              disabled={loading}
              style={{
                background: viewMode === 'grid' ? 'var(--gold)' : 'transparent',
                color: viewMode === 'grid' ? 'var(--navy)' : 'var(--text)',
                border: viewMode === 'grid' ? 'none' : '1px solid var(--border)'
              }}
            >
              <LayoutGrid size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Grid
            </button>
            <button
              className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-ghost'} btn-sm`}
              onClick={() => setViewMode('list')}
              disabled={loading}
              style={{
                background: viewMode === 'list' ? 'var(--gold)' : 'transparent',
                color: viewMode === 'list' ? 'var(--navy)' : 'var(--text)',
                border: viewMode === 'list' ? 'none' : '1px solid var(--border)'
              }}
            >
              <List size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />List
            </button>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedUsers.length > 0 && (
          <div style={{ marginTop: '12px', padding: '10px 14px', background: 'var(--blue-bg)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: '500' }}>
              {selectedUsers.length} user(s) selected
            </span>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button className="btn btn-sm btn-gold" onClick={() => handleBulkAction('activate')} title="Activate selected users" disabled={loading || isSubmitting}><CheckCircle2 size={13} style={{ verticalAlign: '-2px', marginRight: '3px' }} />Activate</button>
              <button className="btn btn-sm btn-ghost" onClick={() => handleBulkAction('deactivate')} title="Deactivate selected users" disabled={loading || isSubmitting}><Pause size={13} style={{ verticalAlign: '-2px', marginRight: '3px' }} />Deactivate</button>
              <button className="btn btn-sm btn-danger" onClick={() => handleBulkAction('block')} title="Block selected users" disabled={loading || isSubmitting}><Ban size={13} style={{ verticalAlign: '-2px', marginRight: '3px' }} />Block</button>
              <button className="btn btn-sm btn-danger" style={{ background: 'var(--red-bg)', color: 'var(--red)', border: '1px solid #fca5a5' }} onClick={() => handleBulkAction('delete')} title="Delete selected users permanently" disabled={loading || isSubmitting}><Trash2 size={13} style={{ verticalAlign: '-2px', marginRight: '3px' }} />Delete</button>
            </div>
          </div>
        )}
      </div>

      {/* User Grid/List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Loader2 size={40} style={{ animation: 'spin 1s linear infinite', color: 'var(--text-light)' }} />
          <div style={{ color: 'var(--text-light)', marginTop: '8px' }}>Loading users...</div>
        </div>
      ) : (
        <>
          {users.length > 0 ? (
            <>
              {viewMode === 'grid' ? renderGridView() : renderListView()}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination-controls" style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: '16px',
                  marginTop: '20px',
                  padding: '16px',
                  background: 'var(--white)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)',
                  boxShadow: 'var(--shadow)'
                }}>
                  <div style={{ fontSize: '13px', color: 'var(--text-light)' }}>
                    Showing <strong>{((currentPage - 1) * itemsPerPage) + 1}</strong> to <strong>{Math.min(currentPage * itemsPerPage, totalItems)}</strong> of <strong>{totalItems}</strong> users
                    {selectedUsers.length > 0 && (
                      <span style={{ marginLeft: '8px', fontWeight: '600', color: 'var(--navy)' }}>
                        · {selectedUsers.length} selected
                      </span>
                    )}
                  </div>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <label style={{ fontSize: '12px', color: 'var(--text-light)' }}>Show:</label>
                      <select 
                        className="form-select" 
                        style={{ width: 'auto', padding: '4px 8px', fontSize: '12px' }}
                        value={itemsPerPage}
                        onChange={(e) => {
                          setItemsPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        disabled={loading}
                      >
                        <option value={8}>8</option>
                        <option value={12}>12</option>
                        <option value={24}>24</option>
                        <option value={48}>48</option>
                      </select>
                    </div>
                    
                    <div className="page-numbers" style={{ display: 'flex', gap: '4px' }}>
                      <button 
                        className="btn btn-ghost btn-sm"
                        onClick={goToPreviousPage}
                        disabled={currentPage === 1 || loading}
                        style={{ opacity: currentPage === 1 ? 0.5 : 1, cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
                        title="Previous page"
                      >
                        ← Prev
                      </button>
                      
                      {getPageNumbers().map(page => (
                        <button 
                          key={page}
                          className={`btn btn-sm ${page === currentPage ? 'btn-primary' : 'btn-ghost'}`}
                          onClick={() => goToPage(page)}
                          disabled={loading}
                          title={`Go to page ${page}`}
                        >
                          {page}
                        </button>
                      ))}
                      
                      <button 
                        className="btn btn-ghost btn-sm"
                        onClick={goToNextPage}
                        disabled={currentPage === totalPages || loading}
                        style={{ opacity: currentPage === totalPages ? 0.5 : 1, cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
                        title="Next page"
                      >
                        Next →
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="empty-state">
              <div className="empty-icon"><UserIcon size={32} strokeWidth={1.5} /></div>
              <div className="empty-title">No users found</div>
              <div className="empty-text">Try adjusting your search or filters</div>
            </div>
          )}
        </>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .form-input.error, .form-select.error {
          border-color: var(--red);
          background-color: var(--red-bg);
        }
        .form-input.error:focus, .form-select.error:focus {
          border-color: var(--red);
          box-shadow: 0 0 0 3px rgba(192, 57, 43, 0.15);
        }
        .form-error {
          color: var(--red);
          font-size: 11px;
          margin-top: 4px;
        }
        .table-wrap {
          overflow-x: auto;
        }
        table {
          width: 100%;
          border-collapse: collapse;
        }
        th {
          text-align: left;
          padding: 10px 12px;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--text-light);
          border-bottom: 2px solid var(--border);
          background: var(--cream);
        }
        td {
          padding: 10px 12px;
          border-bottom: 1px solid var(--border-light);
          font-size: 13px;
        }
        tr:hover td {
          background: var(--cream);
        }
        
        /* ==================== CUSTOM BUTTON STYLES ==================== */
        .btn-approve, .btn-approve-sm {
          background: var(--green-bg) !important;
          color: var(--green) !important;
          border: 1px solid #a7d7be !important;
          padding: 6px 14px !important;
          font-size: 12px !important;
          border-radius: 6px !important;
          cursor: pointer !important;
          transition: all 0.2s ease !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          font-weight: 500 !important;
        }
        .btn-approve:hover, .btn-approve-sm:hover {
          background: #22c55e !important;
          color: white !important;
          border-color: #22c55e !important;
        }

        .btn-reject, .btn-reject-sm {
          background: var(--red-bg) !important;
          color: var(--red) !important;
          border: 1px solid #fca5a5 !important;
          padding: 6px 14px !important;
          font-size: 12px !important;
          border-radius: 6px !important;
          cursor: pointer !important;
          transition: all 0.2s ease !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          font-weight: 500 !important;
        }
        .btn-reject:hover, .btn-reject-sm:hover {
          background: var(--red) !important;
          color: white !important;
          border-color: var(--red) !important;
        }

        .btn-activate, .btn-activate-sm {
          background: var(--green-bg) !important;
          color: var(--green) !important;
          border: 1px solid #a7d7be !important;
          padding: 6px 14px !important;
          font-size: 12px !important;
          border-radius: 6px !important;
          cursor: pointer !important;
          transition: all 0.2s ease !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          font-weight: 500 !important;
        }
        .btn-activate:hover, .btn-activate-sm:hover {
          background: #22c55e !important;
          color: white !important;
          border-color: #22c55e !important;
        }

        .btn-deactivate, .btn-deactivate-sm {
          background: var(--amber-bg) !important;
          color: var(--amber) !important;
          border: 1px solid #fcd34d !important;
          padding: 6px 14px !important;
          font-size: 12px !important;
          border-radius: 6px !important;
          cursor: pointer !important;
          transition: all 0.2s ease !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          font-weight: 500 !important;
        }
        .btn-deactivate:hover, .btn-deactivate-sm:hover {
          background: var(--amber) !important;
          color: white !important;
          border-color: var(--amber) !important;
        }

        .btn-danger-sm {
          background: var(--red-bg) !important;
          color: var(--red) !important;
          border: 1px solid #fca5a5 !important;
          padding: 4px 10px !important;
          font-size: 11px !important;
          border-radius: 6px !important;
          cursor: pointer !important;
          transition: all 0.2s ease !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          font-family: inherit !important;
        }
        .btn-danger-sm:hover {
          background: var(--red) !important;
          color: white !important;
          border-color: var(--red) !important;
        }

        .btn-unblock, .btn-unblock-sm {
          background: var(--green-bg) !important;
          color: var(--green) !important;
          border: 1px solid #a7d7be !important;
          padding: 6px 14px !important;
          font-size: 12px !important;
          border-radius: 6px !important;
          cursor: pointer !important;
          transition: all 0.2s ease !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          font-weight: 500 !important;
        }
        .btn-unblock:hover, .btn-unblock-sm:hover {
          background: #22c55e !important;
          color: white !important;
          border-color: #22c55e !important;
        }

        .btn-delete, .btn-delete-sm {
          color: #ef4444 !important;
          background: transparent !important;
          border: none !important;
          padding: 6px 10px !important;
          font-size: 12px !important;
          border-radius: 6px !important;
          cursor: pointer !important;
          transition: all 0.2s ease !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          font-weight: 500 !important;
        }
        .btn-delete:hover, .btn-delete-sm:hover {
          background: #fef2f2 !important;
          color: #ef4444 !important;
        }

        .btn-approve-sm, .btn-reject-sm, .btn-activate-sm, 
        .btn-deactivate-sm, .btn-unblock-sm, .btn-delete-sm {
          padding: 4px 10px !important;
          font-size: 11px !important;
          min-width: 28px !important;
          min-height: 28px !important;
        }

        .btn-gold {
          background: var(--gold) !important;
          color: var(--navy) !important;
          border: none !important;
        }
        .btn-gold:hover {
          background: var(--gold-dark) !important;
          color: var(--navy) !important;
        }

        .btn-danger {
          background: var(--red) !important;
          color: white !important;
          border: none !important;
        }
        .btn-danger:hover {
          background: var(--red-dark) !important;
          color: white !important;
        }

        .btn-ghost {
          background: transparent !important;
          color: var(--text) !important;
          border: 1px solid var(--border) !important;
        }
        .btn-ghost:hover {
          background: var(--cream) !important;
          color: var(--text) !important;
        }

        .btn-primary {
          background: var(--gold) !important;
          color: var(--navy) !important;
          border: none !important;
        }
        .btn-primary:hover {
          background: var(--gold-dark) !important;
          color: var(--navy) !important;
        }

        .btn-sm {
          padding: 4px 10px !important;
          font-size: 12px !important;
        }
        
        .btn-sm.btn-icon {
          padding: 4px 6px !important;
        }

        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        @media (max-width: 1024px) {
          .user-management .stats-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .user-management .user-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .user-management .pagination-controls { flex-direction: column !important; align-items: center !important; }
        }
        @media (max-width: 600px) {
          .user-management .stats-grid { grid-template-columns: 1fr !important; }
          .user-management .user-grid { grid-template-columns: 1fr !important; }
          .user-management .filter-container .search-box { max-width: 100% !important; }
          .user-management .filter-container select { width: 100% !important; }
          .user-management .pagination-controls { flex-direction: column !important; align-items: center !important; }
          .user-management .pagination-controls .page-numbers { flex-wrap: wrap !important; justify-content: center !important; }
        }
      `}</style>
    </div>
  );
};

export default User;